import { Dtos, FinancialPeriodPreparationMethod, type ISODate, PeriodType } from "@inrev/common";
import { DateTime } from "luxon";
import { BalanceSheetWithView, IncomeStatementWithView } from "../types";

type StatementSortable = {
	primaryDate: ISODate;
	periodType?: PeriodType;
	preparationMethod: FinancialPeriodPreparationMethod;
	sourceStatementDate?: ISODate;
	createdAt: string;
	id: string;
};

export const isBalanceSheetWithView = (
	bs: Dtos.Admin.BalanceSheet.Get.Response,
): bs is BalanceSheetWithView => bs.view !== undefined;

export const isIncomeStatementWithView = (
	is: Dtos.Admin.IncomeStatement.Get.Response,
): is is IncomeStatementWithView => is.view !== undefined;

const sourceStatementDateMillis = (iso: ISODate | undefined): number | null =>
	iso ? DateTime.fromISO(iso).toMillis() : null;

/** Tie-break revisions: newer source statement date first, then newer upload (createdAt). */
const compareSourceStatementDateThenCreatedAt = (
	a: Pick<StatementSortable, "sourceStatementDate" | "createdAt">,
	b: Pick<StatementSortable, "sourceStatementDate" | "createdAt">,
): number => {
	const aStmt = sourceStatementDateMillis(a.sourceStatementDate);
	const bStmt = sourceStatementDateMillis(b.sourceStatementDate);
	if (aStmt !== null && bStmt !== null && aStmt !== bStmt) {
		return bStmt - aStmt;
	}
	if (aStmt !== null && bStmt === null) return -1;
	if (aStmt === null && bStmt !== null) return 1;
	return b.createdAt.localeCompare(a.createdAt);
};

const compareFinancialStatementsForColumns = (
	a: StatementSortable,
	b: StatementSortable,
	options: { rankPeriodType: boolean },
): number => {
	const dateDiff = b.primaryDate.localeCompare(a.primaryDate);
	if (dateDiff !== 0) return dateDiff;

	if (options.rankPeriodType) {
		const typeDiff = PeriodType.rank(a.periodType) - PeriodType.rank(b.periodType);
		if (typeDiff !== 0) return typeDiff;
	}

	const qualityDiff =
		FinancialPeriodPreparationMethod.qualityRank(a.preparationMethod) -
		FinancialPeriodPreparationMethod.qualityRank(b.preparationMethod);
	if (qualityDiff !== 0) return qualityDiff;

	const revisionDiff = compareSourceStatementDateThenCreatedAt(a, b);
	if (revisionDiff !== 0) return revisionDiff;

	return a.id.localeCompare(b.id);
};

const toBalanceSheetSortable = (bs: Dtos.Admin.BalanceSheet.Get.Response): StatementSortable => ({
	primaryDate: bs.statementDate,
	preparationMethod: bs.preparationMethod,
	sourceStatementDate: bs.sourceStatementDate,
	createdAt: bs.createdAt,
	id: bs.id,
});

const toIncomeStatementSortable = (
	is: Dtos.Admin.IncomeStatement.Get.Response,
): StatementSortable => ({
	primaryDate: is.periodEndDate,
	periodType: is.periodType,
	preparationMethod: is.preparationMethod,
	sourceStatementDate: is.sourceStatementDate,
	createdAt: is.createdAt,
	id: is.id,
});

/** Column order for balance sheet matrix and period selector (newest / best first). */
export const sortBalanceSheets = (
	balanceSheets: Dtos.Admin.BalanceSheet.Get.Response[],
): Dtos.Admin.BalanceSheet.Get.Response[] =>
	[...balanceSheets].sort((a, b) =>
		compareFinancialStatementsForColumns(toBalanceSheetSortable(a), toBalanceSheetSortable(b), {
			rankPeriodType: false,
		}),
	);

/** Column order for income statement matrix and period selector (newest / best first). */
export const sortIncomeStatements = (
	incomeStatements: Dtos.Admin.IncomeStatement.Get.Response[],
): Dtos.Admin.IncomeStatement.Get.Response[] =>
	[...incomeStatements].sort((a, b) =>
		compareFinancialStatementsForColumns(
			toIncomeStatementSortable(a),
			toIncomeStatementSortable(b),
			{ rankPeriodType: true },
		),
	);

export const sortedUsableBalanceSheets = (
	balanceSheets: Dtos.Admin.BalanceSheet.Get.Response[],
): BalanceSheetWithView[] => sortBalanceSheets(balanceSheets).filter(isBalanceSheetWithView);

export const sortedUsableIncomeStatements = (
	incomeStatements: Dtos.Admin.IncomeStatement.Get.Response[],
): IncomeStatementWithView[] =>
	sortIncomeStatements(incomeStatements).filter(isIncomeStatementWithView);
